import { useState } from 'react';
import type { ReactElement } from 'react';
import { Dialog } from '../../components/Dialog.js';
import type {
  CalendarEntry,
  CalendarDaySummary,
  MealSlot,
} from '../../api/calendarTypes.js';
import { MEAL_ICON, MEAL_LABEL } from '../../api/calendarTypes.js';
import { DayMacrosLine } from './DayMacrosLine.js';
import { MealCard } from './MealCard.js';
import { addDays, formatLongDate, slotEntriesByDate } from './calendarUtils.js';
import { AddMealDialog } from './AddMealDialog.js';

export interface DayDetailDialogProps {
  open: boolean;
  date: string;
  entries: CalendarEntry[];
  summary: CalendarDaySummary;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onOpenRecipe: (id: number) => void;
  onCook: (entry: CalendarEntry) => void;
  onEditMeal: (entry: CalendarEntry) => void;
  onAddMeal: (slot: MealSlot, recipeId: number) => Promise<void>;
  onRemoveMeal: (entryId: number) => Promise<void>;
  onRetry: () => void;
  onPrevDay: () => void;
  onNextDay: () => void;
}

export function DayDetailDialog({
  open,
  date,
  entries,
  summary,
  loading,
  error,
  onClose,
  onOpenRecipe,
  onCook,
  onEditMeal,
  onAddMeal,
  onRemoveMeal,
  onRetry,
  onPrevDay,
  onNextDay,
}: DayDetailDialogProps): ReactElement {
  const [addSlot, setAddSlot] = useState<MealSlot | null>(null);
  const [busySlot, setBusySlot] = useState<MealSlot | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const slotMap = slotEntriesByDate(entries, date);
  const slots: MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack'];

  async function handleAdd(slot: MealSlot, recipeId: number): Promise<void> {
    setBusySlot(slot);
    try {
      await onAddMeal(slot, recipeId);
    } finally {
      setBusySlot(null);
    }
  }

  async function handleRemove(entry: CalendarEntry): Promise<void> {
    setRemovingId(entry.id);
    try {
      await onRemoveMeal(entry.id);
    } finally {
      setRemovingId(null);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={formatLongDate(date)}
      >
        <div className="flex flex-col gap-3" data-testid="day-detail-dialog">
          <div
            className="flex items-center justify-between"
            data-testid="day-detail-daynav"
          >
            <button
              type="button"
              onClick={onPrevDay}
              aria-label={`Previous day (${formatLongDate(addDays(date, -1))})`}
              className="flex min-h-[40px] items-center justify-center rounded-md border px-3 text-sm font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--text-main)', background: 'transparent' }}
              data-testid="day-detail-prev-day"
            >
              ‹ Prev
            </button>
            <button
              type="button"
              onClick={onNextDay}
              aria-label={`Next day (${formatLongDate(addDays(date, 1))})`}
              className="flex min-h-[40px] items-center justify-center rounded-md border px-3 text-sm font-medium"
              style={{ borderColor: 'var(--border)', color: 'var(--text-main)', background: 'transparent' }}
              data-testid="day-detail-next-day"
            >
              Next ›
            </button>
          </div>
          <DayMacrosLine summary={summary} />

          {error ? (
            <div
              className="flex flex-col items-center gap-2 rounded-md border p-3 text-sm"
              style={{
                borderColor: 'var(--border)',
                color: 'var(--text-muted)',
                background: 'color-mix(in srgb, #dc2626 8%, transparent)',
              }}
              data-testid="day-detail-error"
              role="alert"
            >
              <span>Failed to load agenda: {error}</span>
              <button
                type="button"
                onClick={() => void onRetry()}
                className="flex min-h-[40px] items-center justify-center rounded-md border px-3 text-sm font-medium"
                style={{
                  borderColor: 'var(--accent)',
                  color: 'var(--accent)',
                  background: 'transparent',
                }}
                data-testid="day-detail-retry"
              >
                Retry
              </button>
            </div>
          ) : null}

          <div className="flex flex-col gap-3" data-testid="day-detail-slots">
            {slots.map((slot) => {
              const list = slotMap.get(slot) ?? [];
              return (
                <div
                  key={slot}
                  className="flex flex-col gap-2"
                  data-testid={`day-detail-slot-${slot}`}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="text-sm font-semibold"
                      style={{ color: 'var(--text-main)' }}
                    >
                      <span aria-hidden="true">{MEAL_ICON[slot]}</span> {MEAL_LABEL[slot]}
                    </div>
                    <button
                      type="button"
                      onClick={() => setAddSlot(slot)}
                      disabled={busySlot !== null}
                      className="flex min-h-[36px] items-center justify-center rounded-md border px-2 text-xs font-medium"
                      style={{
                        borderColor: 'var(--accent)',
                        color: 'var(--accent)',
                        background: 'color-mix(in srgb, var(--accent) 10%, transparent)',
                      }}
                      data-testid={`day-detail-add-${slot}`}
                    >
                      + Add
                    </button>
                  </div>
                  {list.length === 0 ? (
                    <div
                      className="flex min-h-[64px] flex-col items-center justify-center gap-1 rounded-xl border border-dashed p-2 text-center text-xs"
                      style={{
                        borderColor: 'var(--border)',
                        color: 'var(--text-muted)',
                        background: 'transparent',
                      }}
                      data-testid={`day-detail-slot-${slot}-empty`}
                    >
                      No meal planned
                    </div>
                  ) : (
                    list.map((entry) => (
                      <MealCard
                        key={entry.id}
                        slot={slot}
                        entry={entry}
                        onOpenRecipe={onOpenRecipe}
                        onCook={onCook}
                        onEdit={onEditMeal}
                        onRemove={
                          removingId === entry.id
                            ? undefined
                            : () => void handleRemove(entry)
                        }
                        removing={removingId === entry.id}
                      />
                    ))
                  )}
                </div>
              );
            })}
          </div>

          {loading && entries.length === 0 ? (
            <div
              className="text-sm"
              style={{ color: 'var(--text-muted)' }}
              data-testid="day-detail-loading"
            >
              Loading…
            </div>
          ) : null}
        </div>
      </Dialog>
      <AddMealDialog
        open={addSlot !== null}
        slot={addSlot ?? 'breakfast'}
        onClose={() => setAddSlot(null)}
        onPick={(recipeId) => {
          if (addSlot) return handleAdd(addSlot, recipeId);
          return Promise.resolve();
        }}
      />
    </>
  );
}
